import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';

import SearchFilled from '../../assets/search-filled.svg';
import useDebounce from '../../hooks/useDebounce';
import { getRequestJSON } from '../../util/services';

type ContractSearchProps = {
  host: string;
  path: string;
  placeholder: string;
  // What to do with the chosen name. Kept context-agnostic so the same input
  // works in the universal header (navigate to the filtered list) and inside the
  // embedded contract picker (filter the picker's table via ContractsContext).
  onSelect: (name: string) => void;
};

function ContractSearch({ host, path, placeholder, onSelect }: ContractSearchProps) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [results, setResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  // Hand the chosen name to the parent, then reset the input — the header search
  // navigates away, so the field shouldn't hold the old term afterward.
  const handleSelect = (name: string) => {
    onSelect(name);
    setSearchTerm('');
    setResults([]);
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 200);
  // Effect for API call
  useEffect(() => {
    if (debouncedSearchTerm) {
      if (!hasSearched) setHasSearched(true);
      setIsSearching(true);
      const searchFinal = `${path}${debouncedSearchTerm}`;
      getRequestJSON(host, searchFinal).then((data) => {
        setIsSearching(false);
        setResults(data.contracts);
      });
    } else {
      setResults([]);
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, hasSearched, host, path]);

  return (
    <Autocomplete
      freeSolo
      value={searchTerm}
      options={results}
      size="small"
      getOptionLabel={(option) => option.name || searchTerm}
      renderOption={(props, option) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <li
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          key={option.id}
          onClick={() => handleSelect(option.name)}
        >
          {option.name || ''}
        </li>
      )}
      style={{ width: 160 }}
      onInputChange={(event, value) => {
        setSearchTerm(value);
      }}
      onKeyDown={(event: any) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (event.target.value) {
            handleSelect(event.target.value);
          }
        }
      }}
      clearIcon={isSearching ? <CircularProgress color="inherit" size={16} /> : undefined}
      renderInput={(params) => (
        <TextField
          sx={{ backgroundColor: 'white' }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          variant="outlined"
          size="small"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: <img src={SearchFilled} alt="Search" />,
          }}
        />
      )}
    />
  );
}

export default ContractSearch;
